import React, { useState, useEffect } from "react";
import { 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  Switch, 
  Space, 
  Popconfirm,
  message,
  Card,
  Row,
  Col,
  Typography,
  Tooltip,
  Tag
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  KeyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined
} from '@ant-design/icons';
import Api from "../../../../services/api";

const { Title } = Typography;

function Permission() {
  const [data, setData] = useState([]);
  const [permission, setPermission] = useState({});
  const [modal, setModal] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const handleChangePage = (page) => {
    setPage(page);
  }

  const getData = () => {
    setLoading(true);
    Api.get(`/api/permission?page=${page}`)
      .then(result => {
        if (result?.data?.data.length > 0) {
          setData(result.data);
        }
        setLoading(false)
      });
  }

  const handleEditPermission = (item) => {
    setPermission({
      id: item.id,
      name: item.name,
      code: item.code,
      status: item.status == 'ACTIVE' ? true : false,
    });
    form.setFieldsValue({
      name: item.name,
      code: item.code,
      status: item.status == 'ACTIVE',
    });
    setModal(true);
  }

  const handleCreatePermission = () => {
    setPermission({});
    form.resetFields();
    setModal(true);
  }

  const saveData = () => {
    form.validateFields().then(values => {
      setLoading(true);
      const api = permission?.id ? Api.put(`/api/permission/${permission.id}`, {
        name: values.name,
        code: values.code,
        status: values.status ? 1 : 0,
      }) : Api.post(`/api/permission`, {
        name: values.name,
        code: values.code,
        status: values.status ? 1 : 0,
      })
      
      api.then(result => {
        setLoading(false)
        if (result.data.code == 0) {
          message.success(permission?.id ? 'Cập nhật permission thành công!' : 'Tạo permission thành công!');
          getData()
          setPermission({})
          setModal(false)
          form.resetFields();
        } else {
          message.error('Có lỗi xảy ra!');
        }
      }).catch(error => {
        setLoading(false);
        message.error('Có lỗi xảy ra!');
      });
    });
  }

  const handleDelete = (item) => {
    setLoading(true);
    Api.delete(`/api/permission/${item.id}`)
      .then(result => {
        setLoading(false);
        if (result.data.code == 0) {
          message.success('Xóa permission thành công!');
          getData()
        } else {
          message.error('Có lỗi xảy ra!');
        }
      }).catch(error => {
        setLoading(false);
        message.error('Có lỗi xảy ra!');
      });
  }

  const columns = [
    {
      title: 'Tên Permission',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <KeyOutlined style={{ color: '#1890ff' }} />
          <span style={{ fontWeight: 'bold' }}>{text}</span>
        </Space>
      ),
    },
    {
      title: 'Mã Code',
      dataIndex: 'code',
      key: 'code',
      render: (text) => (
        <Tag color="blue">{text}</Tag>
      ),
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag 
          color={status === 'ACTIVE' ? 'green' : 'red'}
          icon={status === 'ACTIVE' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
        >
          {status === 'ACTIVE' ? 'Hoạt động' : 'Không hoạt động'}
        </Tag>
      ),
    },
    {
      title: 'Hành động',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Chỉnh sửa">
            <Button 
              type="primary" 
              icon={<EditOutlined />} 
              size="small"
              onClick={() => handleEditPermission(record)}
            />
          </Tooltip>
          <Tooltip title="Xóa">
            <Popconfirm
              title="Bạn có chắc chắn muốn xóa permission này?"
              description="Tất cả user và role có permission này sẽ bị ảnh hưởng."
              onConfirm={() => handleDelete(record)}
              okText="Có"
              cancelText="Không"
            >
              <Button 
                type="primary" 
                danger 
                icon={<DeleteOutlined />} 
                size="small"
              />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    getData();
  }, [page]);

  return (
    <>
      <div className="background-second-dashboard">
        <section className="main__content-dashbard">
          <div className="container-fluid">
            <Card>
              <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
                <Col>
                  <Title level={4} style={{ margin: 0, fontSize: '18px' }}>
                    Quản lý Permissions
                  </Title>
                </Col>
                <Col>
                  <Button 
                    type="primary" 
                    icon={<PlusOutlined />}
                    onClick={handleCreatePermission}
                    size="small"
                  >
                    Tạo Permission
                  </Button>
                </Col>
              </Row>

              <Table
                columns={columns}
                dataSource={data.data || []}
                rowKey="id"
                loading={loading}
                bordered
                pagination={{
                  current: page,
                  total: data.total || 0,
                  pageSize: data.per_page || 10,
                  onChange: handleChangePage,
                  showSizeChanger: false,
                  showQuickJumper: false,
                  showTotal: (total, range) => 
                    `${range[0]}-${range[1]} của ${total} permission`,
                }}
                scroll={{ x: 600 }}
              />
            </Card>
          </div>
        </section>
      </div>

      <Modal
        title={permission?.id ? 'Chỉnh sửa Permission' : 'Tạo Permission mới'}
        open={modal}
        onCancel={() => {
          setModal(false);
          form.resetFields();
        }}
        onOk={saveData}
        confirmLoading={loading}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            status: true
          }}
        >
          <Form.Item
            label="Tên Permission"
            name="name"
            rules={[{ required: true, message: 'Vui lòng nhập tên permission!' }]}
          >
            <Input placeholder="Nhập tên permission" />
          </Form.Item>
          
          <Form.Item
            label="Mã Code"
            name="code"
            rules={[{ required: true, message: 'Vui lòng nhập mã code!' }]}
          >
            <Input placeholder="Nhập mã code" />
          </Form.Item>
          
          <Form.Item
            label="Trạng thái"
            name="status"
            valuePropName="checked"
          >
            <Switch 
              checkedChildren="Hoạt động" 
              unCheckedChildren="Không hoạt động" 
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export default Permission;